import React from 'react';
import { useTheme } from '@emotion/react';
import { Box, Tooltip, Typography } from '@mui/material';

/**
 * A small set of mutually exclusive choices, shown all at once.
 *
 * Used where a switch would otherwise sit inside a form that has a Save
 * button: a switch reads as "this happens now", which is wrong for anything
 * that only takes effect when the page is published.
 */
const SegmentedControl = ({ value, options = [], onChange, ariaLabel }) => {
    const theme = useTheme();

    return (
        <Box
            role="radiogroup"
            aria-label={ariaLabel}
            sx={{
                display: 'inline-flex',
                padding: '3px',
                gap: '2px',
                borderRadius: '10px',
                border: `1px solid ${theme.palette.customColors.b50}`,
                backgroundColor: theme.palette.customColors.yellow_bg,
            }}
        >
            {options.map((option) => {
                const selected = option.value === value;
                const chip = (
                    <Box
                        key={option.value}
                        role="radio"
                        aria-checked={selected}
                        tabIndex={0}
                        onClick={() => onChange(option.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onChange(option.value);
                            }
                        }}
                        sx={{
                            cursor: 'pointer',
                            padding: '6px 14px',
                            borderRadius: '8px',
                            userSelect: 'none',
                            backgroundColor: selected ? theme.palette.customColors.indigo50 : 'transparent',
                            '&:hover': { backgroundColor: selected ? theme.palette.customColors.indigo50 : '#f4f4f5' },
                            '&:focus-visible': { outline: `2px solid ${theme.palette.customColors.indigo700}`, outlineOffset: '2px' },
                        }}
                    >
                        <Typography sx={{
                            fontSize: '0.875rem',
                            fontWeight: selected ? 700 : 500,
                            color: selected ? theme.palette.customColors.indigo700 : theme.palette.customColors.g200,
                        }}>
                            {option.label}
                        </Typography>
                    </Box>
                );

                return option.hint ? (
                    <Tooltip key={option.value} title={option.hint}>{chip}</Tooltip>
                ) : chip;
            })}
        </Box>
    );
};

export default SegmentedControl;
